// Production BOMs — data access (docs/design/bom-views.md slice 3, ADR-043).
//
//   list       rpc pbom_page(company, status, term, filters, sort, after, limit) —
//              one row per version, keyset (item code, version) on
//              pboms_list_idx; column filters and sort server-side.
//   detail     rpc pbom_document(id) — header, nodes, the item's versions and
//              the latest kits made from it in one round-trip.
//   structure  rpc pbom_structure(company, item) — the manufactured levels
//              through the MBOMs (what a PBOM can hold), for the editor.
//   writes     save_pbom / approve_ / obsolete_ / delete_pbom — one RPC each,
//              expected revision (PT409 on a stale one);
//              create_kit_from_pbom — the kit as an instance of the PBOM.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { sortParam, type ListFilter, type ListSort, type versionStatuses } from "@patronix/domain/list";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export type PbomStatus = (typeof versionStatuses)[number];
export type PbomFilter = "current" | PbomStatus | "all";
export const pbomFilters: PbomFilter[] = ["current", "draft", "approved", "obsolete", "all"];

export interface PbomListParams {
  status: PbomFilter;
  q: string;
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface PbomRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  version: number;
  status: PbomStatus;
  node_count: number;
  approved_at: string | null;
  kit_count: number;
}

export interface PbomNode {
  node_no: number;
  parent_node_no: number | null;
  item_id: string;
  item_code: string;
  item_description: string;
  buffer_days: number;
}

export interface PbomDocument {
  id: string;
  company_id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  version: number;
  status: PbomStatus;
  note: string | null;
  node_count: number;
  revision: number;
  approved_at: string | null;
  approved_by_name: string | null;
  obsoleted_at: string | null;
  created_at: string;
  nodes: PbomNode[];
  versions: { id: string; version: number; status: PbomStatus; approved_at: string | null }[];
  kits: { id: string; number: string | null; name: string; status: "open" | "closed"; planned_start: string }[];
}

export interface StructureNode {
  /** Path of item ids from the item ('/'-joined): unique per position. */
  key: string;
  parent_key: string | null;
  depth: number;
  item_id: string;
  item_code: string;
  item_description: string;
  qty_per: string | number;
  base_uom_code: string;
  lead_time_days: number;
  has_bom: boolean;
}

export interface PbomStructure {
  item_id: string;
  date: string;
  truncated: boolean;
  nodes: StructureNode[];
}

export interface PbomNodeInput {
  node_no: number;
  parent_node_no: number | null;
  item_id: string;
  buffer_days: number;
}

export const PBOM_PAGE = 50;
const STALE = 60_000;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

function noDemo() {
  if (isDemoMode) throw new Error("Production BOMs are not available in demo mode");
}

export async function listPboms(
  companyId: string,
  p: PbomListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = PBOM_PAGE,
): Promise<Page<PbomRow>> {
  if (isDemoMode || !companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("pbom_page", {
      p_company_id: companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (PbomRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getPbom(id: string, signal: AbortSignal): Promise<PbomDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("pbom_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as PbomDocument | null) ?? null;
}

async function getStructure(companyId: string, itemId: string, signal: AbortSignal): Promise<PbomStructure | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("pbom_structure", { p_company_id: companyId, p_item_id: itemId }).abortSignal(signal);
  if (error) throw error;
  return (data as PbomStructure | null) ?? null;
}

export async function savePbom(
  id: string | null,
  companyId: string,
  itemId: string,
  header: { note?: string | null; expected_revision?: number },
  nodes: PbomNodeInput[],
): Promise<{ id: string; version: number; status: PbomStatus; revision: number }> {
  noDemo();
  const { data, error } = await db().rpc("save_pbom", {
    p_id: id,
    p_company_id: companyId,
    p_item_id: itemId,
    p_header: header,
    p_nodes: nodes,
  });
  if (error) throw error;
  return data as { id: string; version: number; status: PbomStatus; revision: number };
}

export type PbomAction = "approve" | "obsolete" | "delete";

export async function pbomAction(id: string, action: PbomAction, revision: number): Promise<void> {
  noDemo();
  const fn = { approve: "approve_pbom", obsolete: "obsolete_pbom", delete: "delete_pbom" }[action];
  const { error } = await db().rpc(fn, { p_id: id, p_expected_revision: revision });
  if (error) throw error;
}

export async function createKitFromPbom(id: string, qty: string, dueDate: string, name: string | null): Promise<{ id: string; number: string }> {
  noDemo();
  const { data, error } = await db().rpc("create_kit_from_pbom", { p_pbom_id: id, p_qty: qty, p_due_date: dueDate, p_name: name });
  if (error) throw error;
  return data as { id: string; number: string };
}

export const pbomKeys = {
  all: ["manufacturing", "pboms"] as const,
  list: (companyId: string, p: PbomListParams) => [...pbomKeys.all, "list", companyId, p] as const,
  detail: (id: string) => [...pbomKeys.all, "detail", id] as const,
  structure: (companyId: string, itemId: string) => [...pbomKeys.all, "structure", companyId, itemId] as const,
};

export function pbomListQuery(companyId: string, p: PbomListParams) {
  return infiniteQueryOptions({
    queryKey: pbomKeys.list(companyId, p),
    queryFn: ({ pageParam, signal }) => listPboms(companyId, p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<PbomRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function pbomDetailQuery(id: string) {
  return queryOptions({ queryKey: pbomKeys.detail(id), queryFn: ({ signal }) => getPbom(id, signal), staleTime: STALE });
}

export function pbomStructureQuery(companyId: string, itemId: string) {
  return queryOptions({
    queryKey: pbomKeys.structure(companyId, itemId),
    queryFn: ({ signal }) => getStructure(companyId, itemId, signal),
    staleTime: 30_000,
  });
}

const rules: [RegExp, string][] = [
  [/already has a draft production BOM/, "pbom.err.draftExists"],
  [/changed by someone else/, "pbom.err.conflict"],
  [/^Only a draft version can be changed/, "pbom.err.notDraft"],
  [/is not an active manufactured item; a production BOM needs one/, "pbom.err.notManufactured"],
  [/^Only an approved production BOM makes kits/, "pbom.err.notApproved"],
  [/^The quantity must be above zero/, "pbom.err.qty"],
  [/^The quantity has more decimals/, "pbom.err.qtyDecimals"],
  [/^Date: within a year back/, "pbom.err.date"],
  [/^Missing permission/, "pbom.err.permission"],
];

export function pbomErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const past = /^The kit would start on (\S+), in the past; choose a due date at least (\d+) days ahead/.exec(message);
  if (past) return tr("pbom.err.past", { date: past[1] ?? "", days: past[2] ?? "" });
  const drift = /^Production BOM: (.+)$/.exec(message);
  if (drift) return tr("pbom.err.structure", { problem: drift[1] ?? "" });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
